// Fast projectile traveling from source to target. Used for Ezreal Q.
// Two phases: travel → impact burst.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const TRAVEL_MS = 220;
const IMPACT_MS = 280;
const TOTAL = TRAVEL_MS + IMPACT_MS;

interface Particle {
  angle: number;
  speed: number;
  size: number;
}

export class ProjectileVFX implements Animation {
  private elapsed = 0;
  private particles: Particle[];
  private impactFired = false;

  constructor(
    private from: Point,
    private to: Point,
    private color: string = "#4af0ff",
    private radius: number = 5,
    private onImpact?: () => void,
  ) {
    this.particles = Array.from({ length: 10 }, () => ({
      angle: Math.random() * Math.PI * 2,
      speed: 40 + Math.random() * 60,
      size: 2 + Math.random() * 3,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    if (!this.impactFired && this.elapsed >= TRAVEL_MS) {
      this.impactFired = true;
      this.onImpact?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed <= TRAVEL_MS) {
      this.drawTravel(ctx);
    } else {
      this.drawImpact(ctx);
    }
  }

  private drawTravel(ctx: CanvasRenderingContext2D): void {
    const t = Easing.easeOutQuad(this.elapsed / TRAVEL_MS);
    const x = this.from.x + (this.to.x - this.from.x) * t;
    const y = this.from.y + (this.to.y - this.from.y) * t;

    const tailX = this.from.x + (this.to.x - this.from.x) * Math.max(0, t - 0.2);
    const tailY = this.from.y + (this.to.y - this.from.y) * Math.max(0, t - 0.2);

    ctx.save();
    ctx.globalAlpha = 0.4;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(x, y);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 16;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, this.radius * 0.6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(x, y, this.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D): void {
    const t = (this.elapsed - TRAVEL_MS) / IMPACT_MS; // 0→1
    const fadeOut = 1 - Easing.easeInQuad(t);
    const ringT = Easing.easeOutQuad(t);

    ctx.save();
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 24;

    // Expanding ring
    const ringR = this.radius * (1 + ringT * 5);
    ctx.globalAlpha = fadeOut * 0.8;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.to.x, this.to.y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    // Core flash
    const flashR = this.radius * (1.5 - t);
    if (flashR > 0) {
      ctx.globalAlpha = fadeOut;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(this.to.x, this.to.y, flashR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Spark particles
    for (const p of this.particles) {
      const dist = p.speed * (t * IMPACT_MS / 1000);
      const px = this.to.x + Math.cos(p.angle) * dist;
      const py = this.to.y + Math.sin(p.angle) * dist;
      ctx.globalAlpha = fadeOut * 0.9;
      ctx.fillStyle = this.color;
      ctx.beginPath();
      ctx.arc(px, py, p.size * (1 - t * 0.5), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
